import React, {useEffect, useState} from 'react';
import {Badge, Card, Col, Table} from 'react-bootstrap';
import Button from '@/components/ui/Button';
import {EVENT_TYPE_CALL, EVENT_TYPE_CHAT, EVENT_TYPE_EMAIL, EVENT_TYPE_FEEDBACK} from '@/utils/eventConstants';
import Icon from '@/components/icons/Icon';
import axios from 'axios';
import {getEventEvaluationUrl} from './utils/evaluationsUrl';
import {Tooltip} from '@/components/tooltips';
import i18n from 'i18n';
import {isModuleEnabled} from '@/utils/modules';
import RenderNoDataAvailable from 'components/utils/DataNotAvailable';
import {
    EventDetails,
    SummaryToDisplay,
    EvaluationItem,
    EventSummaryProps,
    KeyItem,
    AdditionalItem
} from '../../types/event';


interface UserData {
    currentModules?: {
        'addon-automatic-summary'?: boolean;
        [key: string]: boolean | undefined;
    };
}

const EventSummary: React.FC<EventSummaryProps> = ({eventDetails, summaryToDisplay, userData, setActiveTab}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [sortedEvaluations, setSortedEvaluations] = useState<EvaluationItem[]>([]);

    useEffect(() => {
        const fetchEvaluations = async (): Promise<void> => {
            setLoading(true);
            try {
                const response = await axios.get(getEventEvaluationUrl(eventDetails.id));
                if (response.data) {
                    const filteredData = response.data.filter(
                        (item: EvaluationItem) => item.type === 'summary_text_sample'
                    );
                    setSortedEvaluations(filteredData.sort((a: EvaluationItem, b: EvaluationItem) => b.priority - a.priority));
                } else {
                    console.error('API response data is undefined');
                }
            } catch (error) {
                console.error('Error fetching evaluations:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchEvaluations();
    }, [eventDetails.id]);

    const getSummaryTitle = (eventTypeName: string): string => {
        switch (eventTypeName) {
            case EVENT_TYPE_CALL:
                return i18n.t('call_summary.text');
            case EVENT_TYPE_FEEDBACK:
                return i18n.t('feedback_summary.text');
            case EVENT_TYPE_CHAT:
                return i18n.t('chat_summary.text');
            case EVENT_TYPE_EMAIL:
                return i18n.t('email_summary.text');
            default:
                return '';
        }
    };

    const getAspectsTitle = (eventTypeName: string): string => {
        switch (eventTypeName) {
            case EVENT_TYPE_CALL:
                return i18n.t('call_aspects.text');
            case EVENT_TYPE_FEEDBACK:
                return i18n.t('feedback_aspects.text');
            case EVENT_TYPE_CHAT:
                return i18n.t('chat_aspects.text');
            case EVENT_TYPE_EMAIL:
                return i18n.t('email_aspects.text');
            default:
                return '';
        }
    };

    const getPreviewTitle = (eventTypeName: string): string => {
        switch (eventTypeName) {
            case EVENT_TYPE_EMAIL:
                return i18n.t('email_preview.text');
            case EVENT_TYPE_FEEDBACK:
                return i18n.t('feedback_preview.text');
            case EVENT_TYPE_CHAT:
                return i18n.t('chat_preview.text');
            default:
                return '';
        }
    };

    const renderEventSummary = (): JSX.Element => {
        return (
            <Card className="mb-3 custom-border">
                <Table style={{tableLayout: 'fixed', width: '100%'}}>
                    <thead>
                        <tr>
                            <th className="align-content-center">
                                {getSummaryTitle(eventDetails.eventTypeName)}
                            </th>
                        </tr>
                    </thead>
                    {isModuleEnabled(userData, 'addon-automatic-summary') ? (
                        <tbody>
                            {summaryToDisplay?.keys?.map((keyItem: KeyItem) => {
                                const additionalValues = Array.isArray(keyItem.additional)
                                    ? keyItem.additional.filter((item: AdditionalItem) => item.value)
                                    : [];

                                return (
                                    <tr key={keyItem.key}>
                                        <td colSpan={2}>
                                            <div
                                                className="d-flex justify-content-between align-items-center">
                                                <h5 className="d-flex align-items-center m-0">{keyItem.label || RenderNoDataAvailable()}
                                                    <Tooltip
                                                        content={keyItem.description ? keyItem.description.replace(/<[^>]+>/g, '') :
                                                            i18n.t('event_sum_no_desc.text')}
                                                        placement="top"
                                                    >
                                                        <Icon name="mdi-information-outline" className="ml-1 p-0 text-info" size='1em' />
                                                    </Tooltip>
                                                </h5>
                                                {additionalValues.map((additionalItem: AdditionalItem) => (
                                                    <Badge
                                                        key={additionalItem.key}
                                                        pill
                                                        bg='transparent'
                                                        className="px-2 py-1 mr-1"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            border: '1px solid #009efb',
                                                            color: '#009efb',
                                                        }}
                                                    >
                                                        {additionalItem.value}
                                                    </Badge>
                                                ))}
                                            </div>
                                            <span
                                                style={{fontSize: '0.9rem'}}
                                                className="d-block mt-1"
                                            >
                                                {keyItem.value || RenderNoDataAvailable()}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}

                            {sortedEvaluations
                                .filter((evalItem: EvaluationItem) =>
                                    !summaryToDisplay?.keys?.some((keyItem: KeyItem) => keyItem.key === evalItem.key) &&
                                    !summaryToDisplay?.additional?.some((additionalItem: KeyItem) => additionalItem.key === evalItem.key)
                                )
                                .map((sortedItem: EvaluationItem) => {
                                    return (
                                        <tr key={sortedItem.key}>
                                            <td colSpan={2}>
                                                <div
                                                    className="d-flex justify-content-between align-items-center">
                                                    <h5 className="d-flex align-items-center m-0">
                                                        {sortedItem.label || RenderNoDataAvailable()}
                                                        <Tooltip
                                                            content={sortedItem.description
                                                                ? sortedItem.description.replace(/<[^>]+>/g, '')
                                                                : i18n.t('event_sum_no_desc.text')}
                                                            placement="top"
                                                        >
                                                            <Icon name="mdi-information-outline" className="ml-1 p-0 text-info" size='1em' />
                                                        </Tooltip>
                                                    </h5>
                                                </div>

                                                <span style={{fontSize: '0.9rem'}}
                                                    className="d-block mt-1">
                                                    {sortedItem.value || RenderNoDataAvailable()}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    ) : (
                        <tbody>
                            <tr>
                                <td>
                                    <div>
                                        {i18n.t('jt_automatic_summary_not_included.text')}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    )}
                </Table>
            </Card>
        );
    };

    const renderEventAspects = (): JSX.Element => {
        return (
            <Card className="mb-3 custom-border">
                <Table style={{tableLayout: 'fixed', width: '100%'}}>
                    <thead>
                        <tr>
                            <th className="align-content-center">{getAspectsTitle(eventDetails.eventTypeName)}</th>
                        </tr>
                    </thead>

                    {isModuleEnabled(userData, 'addon-automatic-summary') ? (
                        <tbody>
                            {summaryToDisplay?.additional?.map((keyItem: KeyItem) => {
                                const additionalValues = Array.isArray(keyItem.additional)
                                    ? keyItem.additional.filter((item: AdditionalItem) => item.value)
                                    : [];

                                return (
                                    <tr key={keyItem.key}>
                                        <td colSpan={2}>
                                            <div
                                                className="d-flex justify-content-between align-items-center">
                                                <h5 className="d-flex align-items-center m-0">{keyItem.label || RenderNoDataAvailable()}
                                                    <Tooltip
                                                        content={keyItem.description ? keyItem.description.replace(/<[^>]+>/g, '') :
                                                            i18n.t('event_sum_no_desc.text')}
                                                        placement="top"
                                                    >
                                                        <Icon name="mdi-information-outline" className="ml-1 p-0 text-info" size='1em' />
                                                    </Tooltip>
                                                </h5>
                                                {additionalValues.map((additionalItem: AdditionalItem) => (
                                                    <Badge
                                                        key={additionalItem.key}
                                                        pill
                                                        bg='transparent'
                                                        className="px-2 py-1 mr-1"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            border: '1px solid #009efb',
                                                            color: '#009efb',
                                                        }}
                                                    >
                                                        {additionalItem.value}
                                                    </Badge>
                                                ))}
                                            </div>
                                            <span
                                                style={{fontSize: '0.9rem'}}
                                                className="d-block mt-1"
                                            >
                                                {keyItem.value || RenderNoDataAvailable()}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    ) : (
                        <tbody>
                            <tr>
                                <td>
                                    <div>
                                        {i18n.t('jt_automatic_summary_not_included.text')}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    )}
                </Table>
            </Card>
        );
    }

    const previewBody = (): JSX.Element | null => {
        if (!summaryToDisplay?.preview?.[0] || !eventDetails) {return null;}

        const previewLength = summaryToDisplay.preview[0].preview_length || 0;
        const bodyKey = summaryToDisplay.preview[0].body_key;

        if (eventDetails?.eventType?.name === EVENT_TYPE_EMAIL) {
            const content = eventDetails?.[bodyKey] ?? '';
            return (
                <span>
                    {content.substring(0, previewLength)}
                    {content.length > previewLength && '...'}
                </span>
            );
        } else if (eventDetails?.eventType?.name === EVENT_TYPE_CHAT) {
            const chatEvents = eventDetails?.[bodyKey] || [];
            return (
                <>
                    <div>{chatEvents.slice(0, previewLength).join(' ')}</div>
                    {chatEvents.length > previewLength && (
                        <div className="mt-2">
                            {`+ ${chatEvents.length - previewLength} ${i18n.t('conversation_st.text')}`}
                        </div>
                    )}
                </>
            );
        } else if (eventDetails?.eventType?.name === EVENT_TYPE_FEEDBACK) {
            return (
                <>
                    <div>{eventDetails?.[bodyKey] || ''}</div>
                    {(eventDetails?.eventFeedbackLength || 0) > previewLength && (
                        <div className="mt-2">
                            {`+ ${(eventDetails.eventFeedbackLength || 0) - previewLength} ${i18n.t('more_resp.text')}`}
                        </div>
                    )}
                </>
            );
        }
        return null;
    };


    const renderEventPreview = (): JSX.Element | null => {
        if (!summaryToDisplay?.preview?.[0] || !eventDetails) {return null;}

        const headerKey = summaryToDisplay.preview[0].header_key;
        const subjectLength = summaryToDisplay.preview[0].subject_length || 0;
        const headerContent = eventDetails?.[headerKey] ?? '';

        const headerText = headerContent.trim()
            ? (eventDetails?.eventType?.name === EVENT_TYPE_EMAIL ? i18n.t('subject.text') : '') +
            headerContent.substring(0, subjectLength) +
            (headerContent.length > subjectLength ? '...' : '')
            : getPreviewTitle(eventDetails?.eventType?.name);

        return (
            <Card className="mb-3 custom-border">
                <div className="overflow-auto w-100">
                    <Table style={{tableLayout: 'fixed', width: '100%'}}>
                        <thead>
                            <tr>
                                <th colSpan={2} className="align-content-center">
                                    {headerText}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td colSpan={2}>
                                    <div className="d-block">
                                        {previewBody()}
                                        <div className="mt-2">
                                            {typeof setActiveTab === 'function' && (
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="plain-button text-info p-0"
                                                    onClick={() => setActiveTab('conversation')}
                                                >
                                                    {i18n.t('jt_read_more.text')}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
            </Card>
        );
    };


    const eventSummary = (): JSX.Element => (
        <>
            {renderEventSummary()}
            {summaryToDisplay?.additional && renderEventAspects()}
        </>
    );

    const eventPreview = (): JSX.Element => (
        <>
            {renderEventPreview()}
            {summaryToDisplay?.additional?.some(keyItem => keyItem.value) && renderEventAspects()}
        </>
    );

    return (
        summaryToDisplay ? (
            <Col md={6}>
                {eventDetails?.eventType?.name === EVENT_TYPE_CALL ? (
                    eventSummary()
                ) : (
                    summaryToDisplay.preview ? eventPreview() : eventSummary()
                )}
            </Col>
        ) : null
    );
}

export default EventSummary; 